import { useState, useCallback, useRef } from 'react';
import { track } from '@/lib/analytics';
import {
  FloorplanState,
  INITIAL_FLOORPLAN_STATE,
  Project,
  FloorplanHistoryEntry,
  FloorplanTransform,
  FloorplanArrow,
} from '@/types';
import {
  generateFloorplan,
  fetchFloorplanImage,
  saveFloorplanConfig,
  archivePreviousFloorplan,
  archiveAndClearFloorplanState,
  saveFloorplanTransform,
  saveFloorplanArrow,
  uploadFloorplanComposite,
  createVersion,
  setActivePointer,
  fetchVersionsWithMessages,
} from '@/services/floorplanService';
import { supabase, getImageUrl } from '@/lib/supabase';
import { useAppSettings } from '@/hooks/useAppSettings';
import { useAuth } from '@/context/AuthContext';
import type { User } from '@supabase/supabase-js';

export const useFloorplanWorkflow = (
  currentProject: Project | null,
  user: User | null,
) => {
  const [floorplanState, setFloorplanState] = useState<FloorplanState>(INITIAL_FLOORPLAN_STATE);
  // Use ref to access latest state inside async callbacks (avoids stale closures)
  const stateRef = useRef(floorplanState);
  stateRef.current = floorplanState;

  // Feature flag: sanitary vector symbols in the fixture burn-in (admin/live split)
  const { settings: appSettings } = useAppSettings();
  const { isAdmin } = useAuth();
  const useSymbols = isAdmin
    ? appSettings.floorplan_fixture_symbols_admin
    : appSettings.floorplan_fixture_symbols_live;

  const updateFloorplanState = useCallback((updates: Partial<FloorplanState>) => {
    setFloorplanState((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetFloorplanState = useCallback(() => {
    setFloorplanState(INITIAL_FLOORPLAN_STATE);
  }, []);

  // Load existing floorplan from DB (when opening from ProjectDetailsView)
  const loadExistingFloorplan = useCallback(async (projectId: string) => {
    try {
      const dbImage = await fetchFloorplanImage(projectId);
      if (!dbImage) return;

      const previewUrl = dbImage.storage_path ? getImageUrl(dbImage.storage_path) : null;
      const generated2dUrl = dbImage.storage_path_floorplan_2d
        ? getImageUrl(dbImage.storage_path_floorplan_2d)
        : null;
      const generated3dUrl = dbImage.storage_path_floorplan_3d
        ? getImageUrl(dbImage.storage_path_floorplan_3d)
        : null;
      const transform2d = dbImage.floorplan_2d_transform ?? null;
      const transform3d = dbImage.floorplan_3d_transform ?? null;
      const arrow2d = dbImage.floorplan_2d_entry_arrow ?? null;
      const arrow3d = dbImage.floorplan_3d_entry_arrow ?? null;
      const branded2dPath = dbImage.storage_path_floorplan_2d_branded ?? null;
      const branded3dPath = dbImage.storage_path_floorplan_3d_branded ?? null;
      const branded2dUrl = branded2dPath ? getImageUrl(branded2dPath) : null;
      const branded3dUrl = branded3dPath ? getImageUrl(branded3dPath) : null;

      // D.2: load versions + messages for turn-by-turn refinement
      const { versions, messagesByVersion } = await fetchVersionsWithMessages(dbImage.id);

      setFloorplanState({
        ...INITIAL_FLOORPLAN_STATE,
        imageDbId: dbImage.id,
        originalStoragePath: dbImage.storage_path,
        previewUrl,
        config: dbImage.floorplan_config || INITIAL_FLOORPLAN_STATE.config,
        generated2dUrl,
        generated2dPath: dbImage.storage_path_floorplan_2d || null,
        generated3dUrl,
        generated3dPath: dbImage.storage_path_floorplan_3d || null,
        transform2d,
        transform3d,
        arrow2d,
        arrow3d,
        branded2dPath,
        branded3dPath,
        branded2dUrl,
        branded3dUrl,
        // D.2 fields
        sourceStoragePath: dbImage.source_storage_path ?? null,
        activeVersionId: dbImage.active_version_id ?? null,
        activeMessageNumber: dbImage.active_message_number ?? null,
        versions,
        messagesByVersion,
      });
    } catch (err) {
      console.error('[FloorplanWorkflow] Failed to load existing floorplan:', err);
    }
  }, []);

  // ========================================================================
  // Generate 2D (single variant)
  // ========================================================================

  const handleGenerate2D = useCallback(
    async (annotatedStoragePath?: string, finalPromptOverride?: string) => {
      if (!currentProject || !user) return;

      const state = stateRef.current;
      const safePath = typeof annotatedStoragePath === 'string' ? annotatedStoragePath : undefined;
      const imagePath = safePath || state.extractedUnitPath || state.originalStoragePath;
      if (!imagePath) return;

      // Archive previous 2D output (if any) before overwriting
      if (state.imageDbId && state.generated2dPath) {
        await archivePreviousFloorplan(state.imageDbId, '2d', state.generated2dPath);
      }

      setFloorplanState((prev) => ({
        ...prev,
        generated2dUrl: null,
        generated2dPath: null,
        generation2dError: null,
        is2dGenerating: true,
        transform2d: null,
        branded2dUrl: null,
        branded2dPath: null,
      }));

      if (state.imageDbId) {
        await saveFloorplanConfig(state.imageDbId, state.config);
      }

      const fixtureTypes = state.config.fixtures
        ? Array.from(new Set(state.config.fixtures.map((f) => f.type)))
        : [];

      const configForCall = {
        flooring: state.config.flooring,
        textHandling: state.config.textHandling,
        roomLabels: state.config.roomLabels,
        roomsLabeled: state.config.roomsLabeled,
        areaFlooring: state.config.areaFlooring,
        fixtureTypes,
      };

      // Burn room labels + fixtures into the image if any are defined
      let effectiveImagePath = imagePath;
      const hasFixtures = state.config.fixtures && state.config.fixtures.length > 0;
      const hasLabels = !state.config.roomsLabeled && state.config.roomLabels.length > 0;

      if (hasFixtures || hasLabels) {
        try {
          const sourceUrl = state.extractedUnitUrl || state.previewUrl;
          if (!sourceUrl) throw new Error('No source image URL for annotation burn-in');

          const { burnAnnotationsIntoImage } = await import('@/lib/imageUtils');
          const { uploadAnnotatedBlob } = await import('@/services/floorplanService');

          const blob = await burnAnnotationsIntoImage(
            sourceUrl,
            state.config.fixtures || [],
            hasLabels ? state.config.roomLabels : [],
            { useSymbols },
          );
          effectiveImagePath = await uploadAnnotatedBlob(
            currentProject.id,
            state.imageDbId || '',
            blob
          );
        } catch (err) {
          console.error('[FloorplanWorkflow] Annotation burn-in failed:', err);
        }
      }

      try {
        const result = await generateFloorplan(
          '2d_generation',
          effectiveImagePath,
          currentProject.id,
          state.imageDbId || undefined,
          user.id,
          configForCall,
          undefined,
          finalPromptOverride
        );

        // D.2: create a new FloorplanVersion + set as active so refinement thread can operate on it
        let newVersion = null;
        if (state.imageDbId && result.storagePath) {
          try {
            newVersion = await createVersion(state.imageDbId, result.storagePath);
            await setActivePointer(state.imageDbId, newVersion.id, null);
          } catch (vErr) {
            console.error('[FloorplanWorkflow] createVersion after 2D gen failed:', vErr);
          }
        }

        setFloorplanState((prev) => ({
          ...prev,
          generated2dUrl: result.imageUrl || null,
          generated2dPath: result.storagePath || null,
          is2dGenerating: false,
          // D.2: wire the new version into state
          ...(newVersion
            ? {
                activeVersionId: newVersion.id,
                activeMessageNumber: null,
                versions: [...prev.versions, newVersion],
                messagesByVersion: { ...prev.messagesByVersion, [newVersion.id]: [] },
              }
            : {}),
        }));
      } catch (err) {
        console.error('[FloorplanWorkflow] 2D generation failed:', err);
        setFloorplanState((prev) => ({
          ...prev,
          is2dGenerating: false,
          generation2dError: err instanceof Error ? err.message : '2D-Generierung fehlgeschlagen',
        }));
      }
    },
    [currentProject, user, useSymbols]
  );

  // ========================================================================
  // Load generation history
  // ========================================================================

  const handleLoadHistory = useCallback(async () => {
    const imageId = stateRef.current.imageDbId;
    if (!imageId) return;

    updateFloorplanState({ isHistoryLoading: true });

    const { data, error } = await supabase
      .from('image_history')
      .select('id, storage_path, created_at, version_type')
      .eq('image_id', imageId)
      .eq('version_type', 'Floorplan2D')
      .order('created_at', { ascending: false })
      .limit(30);

    if (error || !data) {
      console.warn('[FloorplanWorkflow] History fetch failed:', error?.message);
      updateFloorplanState({ isHistoryLoading: false });
      return;
    }

    const entries: FloorplanHistoryEntry[] = data.map((row: Record<string, string>) => ({
      id: row.id,
      storagePath: row.storage_path,
      url: getImageUrl(row.storage_path),
      createdAt: row.created_at,
    }));

    updateFloorplanState({ floorplanHistory: entries, isHistoryLoading: false });
  }, [updateFloorplanState]);

  // ========================================================================
  // Restore a historical image as active
  // ========================================================================

  const handleSelectFromHistory = useCallback((entry: FloorplanHistoryEntry) => {
    updateFloorplanState({
      generated2dUrl: entry.url,
      generated2dPath: entry.storagePath,
    });

    const imageId = stateRef.current.imageDbId;
    if (imageId) {
      supabase
        .from('images')
        .update({ storage_path_floorplan_2d: entry.storagePath })
        .eq('id', imageId)
        .then(({ error }) => {
          if (error) console.warn('[FloorplanWorkflow] Failed to save history selection:', error.message);
        });
    }
  }, [updateFloorplanState]);

  // Apply deterministically cropped unit image (white-out already done client-side)
  const handleUnitExtraction = useCallback(
    async (croppedStoragePath: string, polygonPoints: Array<{ x: number; y: number }>) => {
      if (!currentProject) return;

      // Reset so the false→true transition triggers auto-nav in App.tsx
      updateFloorplanState({ unitExtractionDone: false });

      const imageUrl = getImageUrl(croppedStoragePath);

      // Update DB storage_path to point to the cropped unit image (await flushes the reset above)
      const imageId = stateRef.current.imageDbId;
      if (imageId) {
        const { error } = await supabase
          .from('images')
          .update({ storage_path: croppedStoragePath })
          .eq('id', imageId);
        if (error) {
          console.warn('[FloorplanWorkflow] Failed to update storage_path for unit extraction:', error.message);
        }
      }

      console.log('[FloorplanWorkflow] Unit extraction applied (client-side):', {
        croppedStoragePath,
        imageUrl,
      });

      // Keep originalStoragePath and previewUrl unchanged so going back
      // to unit-selection still shows the full multi-unit floorplan.
      // The editor uses extractedUnitUrl for display.
      updateFloorplanState({
        unitExtractionDone: true,
        extractedUnitUrl: imageUrl,
        extractedUnitPath: croppedStoragePath,
        lastExtractedPolygonKey: JSON.stringify(polygonPoints),
        generated2dUrl: null,
        generated2dPath: null,
        generated3dUrl: null,
        generated3dPath: null,
      });
    },
    [currentProject, updateFloorplanState]
  );

  // ========================================================================
  // Generate 3D (single variant)
  // ========================================================================

  const handleGenerate3D = useCallback(async () => {
    if (!currentProject || !user || !stateRef.current.generated2dPath) return;
    track('Floorplan 3D Generated');

    const state = stateRef.current;

    if (state.imageDbId && state.generated3dPath) {
      await archivePreviousFloorplan(state.imageDbId, '3d', state.generated3dPath);
    }

    setFloorplanState((prev) => ({
      ...prev,
      is3dGenerating: true,
      generation3dError: null,
      generated3dUrl: null,
      generated3dPath: null,
      transform3d: null,
      branded3dUrl: null,
      branded3dPath: null,
    }));

    try {
      const result = await generateFloorplan(
        '3d_generation',
        state.generated2dPath!,
        currentProject.id,
        state.imageDbId || undefined,
        user.id
      );

      setFloorplanState((prev) => ({
        ...prev,
        generated3dUrl: result.imageUrl || null,
        generated3dPath: result.storagePath || null,
        is3dGenerating: false,
      }));
    } catch (err) {
      console.error('[FloorplanWorkflow] 3D generation failed:', err);
      setFloorplanState((prev) => ({
        ...prev,
        is3dGenerating: false,
        generation3dError: err instanceof Error ? err.message : '3D-Generierung fehlgeschlagen',
      }));
    }
  }, [currentProject, user]);

  // ========================================================================
  // Branding: persist transforms + composite uploads (Phase E)
  // ========================================================================

  const handleSave2DTransform = useCallback(async (transform: FloorplanTransform) => {
    const imageId = stateRef.current.imageDbId;
    if (!imageId) return;
    updateFloorplanState({ transform2d: transform });
    try {
      await saveFloorplanTransform(imageId, '2d', transform);
    } catch (err) {
      console.error('[FloorplanWorkflow] saveFloorplanTransform(2d) failed:', err);
    }
  }, [updateFloorplanState]);

  const handleSave3DTransform = useCallback(async (transform: FloorplanTransform) => {
    const imageId = stateRef.current.imageDbId;
    if (!imageId) return;
    updateFloorplanState({ transform3d: transform });
    try {
      await saveFloorplanTransform(imageId, '3d', transform);
    } catch (err) {
      console.error('[FloorplanWorkflow] saveFloorplanTransform(3d) failed:', err);
    }
  }, [updateFloorplanState]);

  const handleSaveArrow2D = useCallback(async (arrow: FloorplanArrow | null) => {
    const imageId = stateRef.current.imageDbId;
    if (!imageId) return;
    updateFloorplanState({ arrow2d: arrow });
    try {
      await saveFloorplanArrow(imageId, '2d', arrow);
    } catch (err) {
      console.error('[FloorplanWorkflow] saveFloorplanArrow(2d) failed:', err);
    }
  }, [updateFloorplanState]);

  const handleSaveArrow3D = useCallback(async (arrow: FloorplanArrow | null) => {
    const imageId = stateRef.current.imageDbId;
    if (!imageId) return;
    updateFloorplanState({ arrow3d: arrow });
    try {
      await saveFloorplanArrow(imageId, '3d', arrow);
    } catch (err) {
      console.error('[FloorplanWorkflow] saveFloorplanArrow(3d) failed:', err);
    }
  }, [updateFloorplanState]);

  const handleSaveComposite = useCallback(async (variant: '2d' | '3d', blob: Blob) => {
    const state = stateRef.current;
    if (!state.imageDbId || !currentProject) return;
    try {
      const storagePath = await uploadFloorplanComposite(
        currentProject.id,
        state.imageDbId,
        variant,
        blob
      );
      const url = getImageUrl(storagePath);
      if (variant === '2d') {
        updateFloorplanState({ branded2dUrl: url, branded2dPath: storagePath });
      } else {
        updateFloorplanState({ branded3dUrl: url, branded3dPath: storagePath });
      }
    } catch (err) {
      console.error(`[FloorplanWorkflow] uploadFloorplanComposite(${variant}) failed:`, err);
      throw err; // Let caller show error banner
    }
  }, [currentProject, updateFloorplanState]);

  // Archive existing 2D+3D to history, clear DB columns, reset local state so user
  // sees a fresh drop-zone. Called from FloorplanUploadView's "Neuen Grundriss hochladen".
  const handleStartOverFloorplan = useCallback(async () => {
    const state = stateRef.current;
    if (!state.imageDbId) return;
    try {
      await archiveAndClearFloorplanState(
        state.imageDbId,
        state.generated2dPath,
        state.generated3dPath
      );
      setFloorplanState(INITIAL_FLOORPLAN_STATE);
    } catch (err) {
      console.error('[FloorplanWorkflow] handleStartOverFloorplan failed:', err);
      alert(err instanceof Error ? err.message : 'Konnte Stand nicht zurücksetzen.');
    }
  }, []);

  return {
    floorplanState,
    updateFloorplanState,
    resetFloorplanState,
    loadExistingFloorplan,
    handleGenerate2D,
    handleLoadHistory,
    handleSelectFromHistory,
    handleUnitExtraction,
    handleGenerate3D,
    handleSave2DTransform,
    handleSave3DTransform,
    handleSaveArrow2D,
    handleSaveArrow3D,
    handleSaveComposite,
    handleStartOverFloorplan,
  };
};
